/*
  viral: <viral-gallery-nav> adds previous / next arrows and a "1 / 3" counter to the product image
  in the desktop thumbnail layouts (snippets/product-media-gallery.liquid). Dawn only has arrows on mobile.
  - On desktop the photo list is a scroll-snap carousel (assets/viral-brand.css), so the arrows slide
    from photo to photo; the counter follows whatever moved it (arrow, swipe, thumbnail or variant).
  - Navigation goes through media-gallery.js (setActiveMedia), so the thumbnails, the live region
    and video playback keep Dawn's own behaviour.
  - The last photo wraps round to the first one and vice versa.
*/
(() => {
  if (customElements.get('viral-gallery-nav')) return;

  class ViralGalleryNav extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      this.list = this.gallery && this.gallery.querySelector('[id^="Slider-Gallery"]');
      this.counter = this.querySelector('[data-viral-gallery-current]');
      if (!this.list) return;

      this.onClick = (event) => {
        const button = event.target.closest('[data-viral-gallery-step]');
        if (button) this.step(Number(button.dataset.viralGalleryStep));
      };
      this.addEventListener('click', this.onClick);

      // The counter follows a swipe / trackpad scroll as well as the arrows.
      this.onScroll = () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateCounter());
      };
      this.list.addEventListener('scroll', this.onScroll, { passive: true });

      // ...and a variant change or a thumbnail click, which only move the is-active class.
      this.observer = new MutationObserver(() => this.updateCounter());
      this.observer.observe(this.list, { attributes: true, attributeFilter: ['class'], subtree: true });

      this.updateCounter();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onClick) this.removeEventListener('click', this.onClick);
      if (this.onScroll && this.list) this.list.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.observer = null;
      this.frame = null;
    }

    slides() {
      return Array.from(this.list.querySelectorAll(':scope > [data-media-id]'));
    }

    // Is the list actually scrolling sideways (desktop carousel / mobile slider)?
    isCarousel() {
      return this.list.scrollWidth > this.list.clientWidth + 8;
    }

    // Each slide's left edge measured inside the list's own scroll box.
    positions(slides) {
      const origin = this.list.getBoundingClientRect().left - this.list.scrollLeft;
      return slides.map((slide) => slide.getBoundingClientRect().left - origin);
    }

    index(slides) {
      if (this.isCarousel()) {
        const left = this.list.scrollLeft;
        let best = 0;
        let distance = Infinity;
        this.positions(slides).forEach((position, i) => {
          const gap = Math.abs(position - left);
          if (gap < distance) {
            distance = gap;
            best = i;
          }
        });
        return best;
      }
      return Math.max(
        0,
        slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
    }

    step(direction) {
      const slides = this.slides();
      if (slides.length < 2) return;
      const next = (this.index(slides) + direction + slides.length) % slides.length;

      if (this.gallery && typeof this.gallery.setActiveMedia === 'function') {
        this.gallery.setActiveMedia(slides[next].dataset.mediaId, false);
      } else if (this.isCarousel()) {
        this.list.scrollTo({ left: this.positions(slides)[next], behavior: 'smooth' });
      }
      this.updateCounter();
    }

    updateCounter() {
      if (!this.counter) return;
      const current = String(this.index(this.slides()) + 1);
      if (this.counter.textContent !== current) this.counter.textContent = current;
    }
  }

  customElements.define('viral-gallery-nav', ViralGalleryNav);
})();
